// @ts-check
import classNames from 'classnames';
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image';
import React from 'react';
import { IsLivePreviewEnabled, useContentStackProps } from '~/contentstack';
import { getImageDisplayOptions } from '~/templates/utils/ImageHelper';
import { cleanDollarFrom } from './cleanDollar';

import './GatsbyBackgroundImage.scss';

/**
 * @typedef GatsbyBackgroundImageProps
 * @property {import('csstype').Property.BackgroundColor} [backgroundColor=transparent]
 * @property {() => void} [onClick]
 * @property {import('~/templates/CSTypes').BynderImageOptions} option
 * @property {string} [keyOfChildImageSharp='gatsbyImageData']
 * @property {import('~/templates/CSTypes').BynderImageOptions} [mobileOption]
 */
/** @type {import('~/types/React').ReactFC<GatsbyBackgroundImageProps>} */
const GatsbyBackgroundImage = ({
	backgroundColor = 'transparent',
	keyOfChildImageSharp = 'gatsbyImageData',
	mobileOption,
	onClick,
	option,
	...rest
}) => {
	const initialOption = cleanDollarFrom(option);

	const [model, modelWith$] = useContentStackProps({
		staticModel: initialOption,
		contentType: 'bynder_image',
		contentUid: initialOption?.uid,
	});
	const [mobileModel] = useContentStackProps(
		{
			staticModel: mobileOption,
			contentType: 'bynder_image',
			contentUid: mobileOption?.uid,
		},
		{ isOptional: true }
	);

	const baseBgOpts = getImageDisplayOptions(model, 100);
	const mobileBgOpts = getImageDisplayOptions(mobileModel, 100);
	if (!baseBgOpts.url) {
		return null;
	}

	/**
	 * No gatsby image means we're in a live preview situation.
	 *
	 * Right now, we are deep merging the changes on top of the static - so we
	 *  never get any updated GatsbyPluginImages (gatsbyImageData).
	 *
	 * TODO: We need to programmatically process (gatsby-plugin-image) a file
	 *  from a new remote URL.
	 *
	 * Naming this 'staticImageData' to remind us that even though we're getting
	 *  it from the model, it will only ever contain the static build data.
	 */
	let staticImageData = null;
	if (initialOption.remoteToLocalImage?.childImageSharp) {
		staticImageData = getImage(initialOption.remoteToLocalImage.childImageSharp[keyOfChildImageSharp]);

		if (mobileOption?.remoteToLocalImage?.childImageSharp) {
			staticImageData = withArtDirection(staticImageData, [
				{
					media: '(max-width: 1024px)',
					image: getImage(mobileOption.remoteToLocalImage.childImageSharp.mobile),
				},
			]);
		}
	}

	const firstClassName = rest.className?.split(' ')?.[0] ?? rest.className;

	const content = (
		<div
			className={classNames(
				'gbi-content',
				{ [`${firstClassName}-content`]: !!rest.className },
				{ 'with-click': !!onClick }
			)}
			onClick={onClick}
			style={{ backgroundColor }}
		>
			{rest.children}
		</div>
	);

	if (IsLivePreviewEnabled || !staticImageData) {
		return (
			<div
				{...rest}
				{...modelWith$?.$?.uid}
				className={classNames('ozk-gatsby-background-image', 'url-based', rest.className)}
				style={{
					backgroundPosition: baseBgOpts.position,
					backgroundSize: baseBgOpts.fit ?? 'cover',
					// Custom css properties to reference in CSS
					// @ts-ignore
					'--mobile-url': `url(${!mobileOption ? baseBgOpts.url : mobileBgOpts.url})`,
					'--desktop-url': `url(${baseBgOpts.url})`,
				}}
				aria-label={baseBgOpts.alt_text}
			>
				{content}
			</div>
		);
	}

	return (
		<div {...rest} {...modelWith$?.$?.uid} className={classNames('ozk-gatsby-background-image', rest.className)}>
			<GatsbyImage
				className="gbi-image"
				alt={baseBgOpts.alt_text}
				title={baseBgOpts.alt_text}
				image={staticImageData}
				objectFit={baseBgOpts.fit}
				objectPosition={baseBgOpts.position}
			/>

			{content}
		</div>
	);
};

export default GatsbyBackgroundImage;
